import { Head } from 'head/Head';
import { Typography } from 'omakase';
import { privacyPolicyLink, termsLink } from 'utils/legal/termsUtils';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';

const ConciergeTermsRoute: React.FunctionComponent = () => {
  const enableAmexTerms = useFeatureFlag('ENABLE_AMEX_TERMS_UPDATES');
  const effectiveDate = enableAmexTerms ? 'October 15, 2024' : 'December 21, 2023';
  return (
    <>
      <Head
        title="Concierge - Terms"
        description="Concierge terms of service"
        keywords="concierge, terms of service, terms, conditions"
      />
      <div className="Legal">
        <div className="Container">
          <Typography component="h1" variant="heading01">
            Concierge Agreement
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            Effective Date: {effectiveDate}
          </Typography>

          <Typography paragraph mb={2}>
            These Concierge Terms (“Concierge Terms”), along with our {termsLink()} or other legal
            terms referenced herein or therein, apply to you if you act as a concierge and use the
            Services to make Bookings on behalf of Guests. These Concierge Terms are incorporated by
            reference into our {termsLink()}. Any capitalized terms (or lowercase terms specifically
            defined in our Terms of Use or Privacy Policy) not defined in these Concierge Terms
            shall have the meanings set forth in our {termsLink()} and/or {privacyPolicyLink()}.
            Except as otherwise expressly set forth in these Concierge Terms, in the event of a
            conflict or inconsistency, our {termsLink()} shall govern, followed by these Concierge
            Terms.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            1. Concierge Services
          </Typography>
          <Typography>
            Concierge may use the Services to make Bookings on behalf of Guests subject to these
            Concierge Terms and our {termsLink()} (including, without limitation, Section 8.4
            thereof). Concierge is solely responsible for ensuring that all Guest information
            (including information of a Guest's partner or other dining companions) which Concierge
            provides to Tock (the “Guest Data”) is accurate, current and complete. Concierge agrees
            that it will not make any Booking on behalf of any Guest unless Concierge: (a)
            communicates to the Guest: (i) that if Concierge proceeds, the Guest Data will be
            provided to Tock and be subject to our {privacyPolicyLink()}; (ii) with respect to any
            Paid Booking, the amount of any deposit or prepayment required by the Merchant; and
            (iii) with respect to each Booking, the applicable Merchant's cancellation policy; and
            (b) has received prior verbal or written consent from the Guest: (i) to provide Tock
            with the Guest Data; (ii) to make the Booking for the applicable time and number of
            covers (including, if applicable, to proceed with any deposit or prepayment). Concierge
            acknowledges and understands that it must use its own credit card to make a Paid Booking
            on behalf of a Guest, unless Concierge and Guest are party to a separate agreement in
            which Guest grants Concierge permission to use Guest's credit card. Concierge agrees
            that it will not make a Booking for a Guest that includes the service or delivery of
            alcohol unless the Guest has confirmed that it is above the minimum legal drinking age
            of the applicable jurisdiction at the time of the Booking.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            2. Chargebacks and Refunds
          </Typography>
          <Typography>
            Concierge agrees that it will not request chargebacks or other transaction reversals for
            Paid Bookings made with Concierge's credit card, including but not limited to refunds,
            in violation of a Merchant's General Merchant Terms or Specific Merchant Booking Terms.
            Concierge agrees to cooperate with Tock if any Guest requests a chargeback or refund in
            violation of a Merchant's General Merchant Terms or Specific Merchant Booking Terms for
            a Paid Booking made using a Guest's credit card. Concierge acknowledges and understands
            that it will be responsible for any chargeback, refund, and any related fees and
            expenses incurred by Tock in connection therewith (“Transaction Reversal Fees”) if a
            Guest alleges: (a) it did not give Concierge permission to provide Tock with the Guest
            Data or make the Paid Booking or (b) Concierge did not communicate to the Guest, or the
            Guest did not provide verbal or written consent to Concierge, as described in Section 1.
            Tock may, in its sole discretion: (i) require a credit card payment from Concierge in
            the amount of any chargeback, refund, or Transaction Reversal Fee; or (b) invoice
            Concierge for the amount of any chargeback, refund, or Transaction Reversal Fee.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            3. Termination
          </Typography>
          <Typography>
            Tock reserves the right to terminate these Concierge Terms or refuse access to the
            Services to you at any time for any reason.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            4. Privacy Policy
          </Typography>
          <Typography>
            Concierge acknowledges and agrees that its provision of Guest Data is subject to Tock's{' '}
            {privacyPolicyLink()}.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            5. Disclaimer
          </Typography>
          <Typography>
            You acknowledge and agree that to the fullest extent permitted by applicable law, the
            Tock Parties make no warranties, express or implied, about use of the Services to make
            Bookings on behalf of Guests, including without limitation any express or implied
            warranty of merchantability or fitness for a particular purpose.
          </Typography>

          <Typography component="h2" variant="heading03" mb={2}>
            6. Modifications to the Concierge Terms
          </Typography>
          <Typography>
            We may modify these Concierge Terms from time to time, and will post the most current
            version on our site. If a modification meaningfully reduces your rights, we may notify
            you in accordance with the procedures set forth in our {termsLink()}. By continuing to
            use or access the Services to make Bookings on behalf of Guests after any modifications
            come into effect, you agree to be bound by the modified Concierge Terms. If you disagree
            with our changes, then you must stop using the Services.
          </Typography>
        </div>
      </div>
    </>
  );
};

export default ConciergeTermsRoute;
